import React, { useEffect, useState } from "react";
import { api, networkLabel } from "../lib/api";
import { useNavigate, useParams } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { ArrowLeft, Save } from "lucide-react";

const DEFAULT_FORM = {
  name: "",
  network: "all",
  account_id: "",
  campaign_ids: [],
  action: "increase",
  target_cpa: 30,
  conversions_threshold: 5,
  cpa_threshold_pct: 50,
  adjustment_pct: 15,
  adjustment_tiers: [],
  lookback_days: 7,
  automation_mode: "manual",
  enabled: true,
  schedule_type: "manual",
  interval_days: 1,
  weekly_days: [],
  weekly_every_n_weeks: 1,
  time_of_day: "09:00",
};

const SUGGESTED_TIERS = [
  { up_to: 50, pct: 30 },
  { up_to: 200, pct: 20 },
  { up_to: 500, pct: 15 },
  { up_to: 1000, pct: 10 },
  { up_to: null, pct: 5 },
];

const WEEKDAYS = [
  { v: "mon", l: "Mon" }, { v: "tue", l: "Tue" }, { v: "wed", l: "Wed" },
  { v: "thu", l: "Thu" }, { v: "fri", l: "Fri" }, { v: "sat", l: "Sat" }, { v: "sun", l: "Sun" },
];

export default function RuleBuilder() {
  const { id } = useParams();
  const editMode = !!id;
  const navigate = useNavigate();
  const [form, setForm] = useState(DEFAULT_FORM);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/accounts").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
    if (editMode) {
      api.get(`/rules/${id}`).then((r) => setForm({
        ...DEFAULT_FORM, ...r.data,
        account_id: r.data.account_id || "",
        weekly_days: r.data.weekly_days || [],
        adjustment_tiers: r.data.adjustment_tiers || [],
      })).catch(() => toast.error("Failed to load rule"));
    }
  }, [id, editMode]);

  const set = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

  const toggleWeekday = (d) => {
    setForm((f) => {
      const has = f.weekly_days.includes(d);
      return { ...f, weekly_days: has ? f.weekly_days.filter((x) => x !== d) : [...f.weekly_days, d] };
    });
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Rule name is required");
    if (form.schedule_type === "weekly" && form.weekly_days.length === 0) {
      return toast.error("Pick at least one day of the week");
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        account_id: form.account_id && form.account_id !== "all" ? form.account_id : null,
        target_cpa: Number(form.target_cpa),
        conversions_threshold: Number(form.conversions_threshold),
        cpa_threshold_pct: Number(form.cpa_threshold_pct),
        adjustment_pct: Number(form.adjustment_pct),
        adjustment_tiers: (form.adjustment_tiers || []).map((t) => ({
          up_to: t.up_to === "" || t.up_to == null ? null : Number(t.up_to),
          pct: Number(t.pct),
        })),
        lookback_days: Number(form.lookback_days),
        interval_days: Number(form.interval_days),
        weekly_every_n_weeks: Number(form.weekly_every_n_weeks),
      };
      if (editMode) {
        await api.put(`/rules/${id}`, payload);
        toast.success("Rule updated");
      } else {
        await api.post(`/rules`, payload);
        toast.success("Rule created");
      }
      navigate("/rules");
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  // Logic preview as natural language
  const tiered = (form.adjustment_tiers || []).length > 0;
  const tiersText = () => (form.adjustment_tiers || [])
    .map((t) => `≤${t.up_to == null || t.up_to === "" ? "∞" : t.up_to}→${t.pct}%`).join(", ");
  const sentence = () => {
    const cond = form.action === "increase"
      ? `CPA < ${form.target_cpa} AND conversions ≥ ${form.conversions_threshold}`
      : form.action === "decrease"
      ? `CPA > ${form.target_cpa}`
      : `CPA > ${form.target_cpa} × (1 + ${form.cpa_threshold_pct}%) = ${(form.target_cpa * (1 + form.cpa_threshold_pct / 100)).toFixed(2)}`;
    const pctPart = tiered ? `by tiered % [${tiersText()}]` : `by ${form.adjustment_pct}%`;
    const act = form.action === "increase"
      ? `INCREASE daily budget ${pctPart}`
      : form.action === "decrease"
      ? `DECREASE daily budget ${pctPart}`
      : `PAUSE campaign`;
    let sched = "ON DEMAND only";
    if (form.schedule_type === "interval") {
      sched = `EVERY ${form.interval_days} DAY(S) AT ${form.time_of_day} UTC`;
    } else if (form.schedule_type === "weekly") {
      const dlist = form.weekly_days.length ? form.weekly_days.map((d) => d.toUpperCase()).join(",") : "—";
      sched = `EVERY ${form.weekly_every_n_weeks} WEEK(S) ON ${dlist} AT ${form.time_of_day} UTC`;
    }
    return `IF ${cond} OVER last ${form.lookback_days} days THEN ${act} · ${sched}`;
  };

  const updateTier = (i, field, value) => {
    setForm((f) => {
      const list = [...(f.adjustment_tiers || [])];
      list[i] = { ...list[i], [field]: value };
      return { ...f, adjustment_tiers: list };
    });
  };
  const addTier = () => setForm((f) => ({
    ...f, adjustment_tiers: [...(f.adjustment_tiers || []), { up_to: null, pct: 10 }],
  }));
  const removeTier = (i) => setForm((f) => ({
    ...f, adjustment_tiers: (f.adjustment_tiers || []).filter((_, idx) => idx !== i),
  }));
  const presetTiers = () => setForm((f) => ({ ...f, adjustment_tiers: SUGGESTED_TIERS }));
  const clearTiers = () => setForm((f) => ({ ...f, adjustment_tiers: [] }));

  return (
    <div data-testid="rule-builder-page" className="fade-in">
      <PageHeader
        eyebrow={editMode ? "Edit Rule" : "New Rule"}
        title={editMode ? "Edit Budget Rule" : "Create Budget Rule"}
        description="Configure thresholds, action, and schedule. Auto-mode pushes changes immediately when criteria match."
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" className="rounded-none" onClick={() => navigate("/rules")} data-testid="back-btn">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={save} disabled={saving} className="rounded-none bg-zinc-900 hover:bg-zinc-800 font-mono uppercase tracking-wider text-xs" data-testid="save-rule-btn">
              <Save className="w-4 h-4 mr-2" />
              {editMode ? "Save changes" : "Create rule"}
            </Button>
          </div>
        }
      />

      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {/* Logic sentence */}
        <div className="bg-zinc-900 text-zinc-100 p-5 font-mono text-sm">
          <div className="data-label text-zinc-400 mb-2">Logic</div>
          <div className="text-emerald-300" data-testid="rule-logic-preview">{sentence()}</div>
        </div>

        {/* Form */}
        <div className="bg-white border border-zinc-200 p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <Label className="data-label">Rule name</Label>
              <Input
                data-testid="rule-name-input"
                value={form.name}
                onChange={(e) => set("name")(e.target.value)}
                placeholder="e.g. Scale winners on Meta UK"
                className="rounded-none border-zinc-300 mt-1"
              />
            </div>
            <div>
              <Label className="data-label">Action</Label>
              <Select value={form.action} onValueChange={set("action")}>
                <SelectTrigger data-testid="rule-action-select" className="rounded-none border-zinc-300 mt-1 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="increase">Increase budget</SelectItem>
                  <SelectItem value="decrease">Decrease budget</SelectItem>
                  <SelectItem value="pause">Pause campaign</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="data-label">Network</Label>
              <Select value={form.network} onValueChange={set("network")}>
                <SelectTrigger data-testid="rule-network-select" className="rounded-none border-zinc-300 mt-1 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All networks</SelectItem>
                  <SelectItem value="meta">Meta only</SelectItem>
                  <SelectItem value="google">Google only</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="data-label">Ad Account (optional)</Label>
              <Select value={form.account_id || "all"} onValueChange={set("account_id")}>
                <SelectTrigger data-testid="rule-account-select" className="rounded-none border-zinc-300 mt-1 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accounts.map((a) => (
                    <SelectItem key={a.account_id} value={a.account_id}>
                      {a.name} ({networkLabel(a.network)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="data-label">Target CPA</Label>
              <Input
                data-testid="rule-target-cpa-input"
                type="number" step="0.01"
                value={form.target_cpa}
                onChange={(e) => set("target_cpa")(e.target.value)}
                className="rounded-none border-zinc-300 mt-1 font-mono"
              />
            </div>
            <div>
              <Label className="data-label">Lookback days</Label>
              <Select value={String(form.lookback_days)} onValueChange={(v) => set("lookback_days")(Number(v))}>
                <SelectTrigger data-testid="rule-lookback-select" className="rounded-none border-zinc-300 mt-1 font-mono">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="14">14 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="60">60 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.action === "increase" && (
              <div>
                <Label className="data-label">Min conversions</Label>
                <Input data-testid="rule-conv-input" type="number" value={form.conversions_threshold}
                  onChange={(e) => set("conversions_threshold")(e.target.value)}
                  className="rounded-none border-zinc-300 mt-1 font-mono" />
              </div>
            )}
            {form.action === "pause" ? (
              <div>
                <Label className="data-label">Pause if CPA over target by %</Label>
                <Input data-testid="rule-pause-pct-input" type="number" value={form.cpa_threshold_pct}
                  onChange={(e) => set("cpa_threshold_pct")(e.target.value)}
                  className="rounded-none border-zinc-300 mt-1 font-mono" />
              </div>
            ) : tiered ? (
              <div className="md:col-span-2">
                <div className="flex items-center justify-between mb-1">
                  <Label className="data-label">Tiered budget adjustments</Label>
                  <div className="flex items-center gap-3 text-[10px] font-mono uppercase">
                    <button type="button" onClick={presetTiers} data-testid="tiers-preset-btn"
                      className="hover:underline text-zinc-600">load suggested</button>
                    <button type="button" onClick={clearTiers} data-testid="tiers-clear-btn"
                      className="hover:underline text-red-600">switch to flat %</button>
                  </div>
                </div>
                <div className="border border-zinc-200 bg-zinc-50">
                  <div className="grid grid-cols-12 px-3 py-2 data-label border-b border-zinc-200 bg-white">
                    <div className="col-span-5">Daily budget tier</div>
                    <div className="col-span-5">Adjust by</div>
                    <div className="col-span-2 text-right">—</div>
                  </div>
                  {(form.adjustment_tiers || []).map((t, i) => (
                    <div key={i} className="grid grid-cols-12 px-3 py-2 items-center gap-2 border-b border-zinc-100" data-testid={`tier-row-${i}`}>
                      <div className="col-span-5 flex items-center gap-2">
                        <span className="text-xs font-mono text-zinc-500">≤ £</span>
                        <Input
                          type="number"
                          value={t.up_to == null ? "" : t.up_to}
                          placeholder="∞ (catch-all)"
                          onChange={(e) => updateTier(i, "up_to", e.target.value === "" ? null : e.target.value)}
                          data-testid={`tier-up-to-${i}`}
                          className="rounded-none border-zinc-300 h-8 font-mono text-xs"
                        />
                      </div>
                      <div className="col-span-5 flex items-center gap-2">
                        <Input
                          type="number"
                          value={t.pct}
                          onChange={(e) => updateTier(i, "pct", e.target.value)}
                          data-testid={`tier-pct-${i}`}
                          className="rounded-none border-zinc-300 h-8 font-mono text-xs"
                        />
                        <span className="text-xs font-mono text-zinc-500">%</span>
                      </div>
                      <div className="col-span-2 text-right">
                        <button type="button" onClick={() => removeTier(i)}
                          data-testid={`tier-remove-${i}`}
                          className="text-[10px] font-mono uppercase text-red-600 hover:underline">
                          remove
                        </button>
                      </div>
                    </div>
                  ))}
                  <div className="px-3 py-2">
                    <button type="button" onClick={addTier}
                      data-testid="tier-add-btn"
                      className="text-[10px] font-mono uppercase tracking-wider text-zinc-700 hover:underline">
                      + add tier
                    </button>
                  </div>
                </div>
                <div className="mt-1 text-[10px] font-mono text-zinc-500">
                  Rows are evaluated top-down. The first tier where the campaign's current daily budget ≤ "up to" value applies.
                  Leave "up to" empty on the last row for a catch-all (e.g. £1,000+).
                </div>
              </div>
            ) : (
              <div>
                <Label className="data-label">Adjust budget by %</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input data-testid="rule-adjust-pct-input" type="number" value={form.adjustment_pct}
                    onChange={(e) => set("adjustment_pct")(e.target.value)}
                    className="rounded-none border-zinc-300 font-mono" />
                  <button type="button" onClick={presetTiers}
                    data-testid="enable-tiers-btn"
                    className="text-[10px] font-mono uppercase tracking-wider text-zinc-700 hover:underline whitespace-nowrap">
                    use tiers
                  </button>
                </div>
                <div className="text-[10px] font-mono text-zinc-500 mt-1">
                  Tip: enable tiers for smaller % changes on large budgets (and larger % changes on small ones).
                </div>
              </div>
            )}
          </div>

          {/* Schedule */}
          <div className="border-t border-zinc-200 pt-5">
            <div className="data-label mb-3">Schedule</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <Label className="data-label">Frequency</Label>
                <Select value={form.schedule_type} onValueChange={set("schedule_type")}>
                  <SelectTrigger data-testid="rule-schedule-type" className="rounded-none border-zinc-300 mt-1 font-mono">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="manual">Manual / on demand</SelectItem>
                    <SelectItem value="interval">Every N days</SelectItem>
                    <SelectItem value="weekly">Specific weekdays</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {form.schedule_type === "interval" && (
                <div>
                  <Label className="data-label">Every</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      data-testid="rule-interval-days"
                      type="number" min="1" max="60"
                      value={form.interval_days}
                      onChange={(e) => set("interval_days")(e.target.value)}
                      className="rounded-none border-zinc-300 font-mono w-24"
                    />
                    <span className="text-sm text-zinc-700 font-mono">day(s)</span>
                  </div>
                </div>
              )}
              {form.schedule_type === "weekly" && (
                <div>
                  <Label className="data-label">Every</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Input
                      data-testid="rule-every-n-weeks"
                      type="number" min="1" max="12"
                      value={form.weekly_every_n_weeks}
                      onChange={(e) => set("weekly_every_n_weeks")(e.target.value)}
                      className="rounded-none border-zinc-300 font-mono w-24"
                    />
                    <span className="text-sm text-zinc-700 font-mono">week(s)</span>
                  </div>
                </div>
              )}
              {form.schedule_type !== "manual" && (
                <div>
                  <Label className="data-label">Time of day (UTC)</Label>
                  <Input
                    data-testid="rule-time-of-day"
                    type="time"
                    value={form.time_of_day}
                    onChange={(e) => set("time_of_day")(e.target.value)}
                    className="rounded-none border-zinc-300 mt-1 font-mono"
                  />
                </div>
              )}
            </div>
            {form.schedule_type === "weekly" && (
              <div className="mt-4">
                <Label className="data-label">Days of week</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {WEEKDAYS.map((d) => {
                    const on = form.weekly_days.includes(d.v);
                    return (
                      <button
                        key={d.v}
                        type="button"
                        data-testid={`rule-weekday-${d.v}`}
                        onClick={() => toggleWeekday(d.v)}
                        className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border ${
                          on
                            ? "bg-zinc-900 text-white border-zinc-900"
                            : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-50"
                        }`}
                      >
                        {d.l}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-zinc-200 pt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="flex items-center justify-between bg-zinc-50 p-4 border border-zinc-200">
              <div>
                <Label className="data-label">Automation</Label>
                <div className="text-sm text-zinc-700 mt-1">
                  {form.automation_mode === "auto"
                    ? "Auto-execute changes (no approval needed)"
                    : "Manual — queue for approval"}
                </div>
              </div>
              <Switch data-testid="rule-automode-switch"
                checked={form.automation_mode === "auto"}
                onCheckedChange={(v) => set("automation_mode")(v ? "auto" : "manual")} />
            </div>
            <div className="flex items-center justify-between bg-zinc-50 p-4 border border-zinc-200">
              <div>
                <Label className="data-label">Enabled</Label>
                <div className="text-sm text-zinc-700 mt-1">
                  Disabled rules will not be run on the schedule or manually.
                </div>
              </div>
              <Switch data-testid="rule-enabled-switch"
                checked={form.enabled} onCheckedChange={set("enabled")} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
